import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Divider,
} from '@mui/material';
import {
  VpnKey as KeyIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  QrCode2 as QrCodeIcon,
  Receipt as ReceiptIcon,
  CheckCircle as PaidIcon,
  WarningAmber as PendingIcon,
  ErrorOutline as OverdueIcon,
  AllInclusive as LifetimeIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { QRCodeSVG } from 'qrcode.react';
import { licenseService, LicenseInfo, LicenseInvoice } from '../../services/licenseService';
import { LicenseModal } from '../../components/LicenseModal';


export const MinhaLicencaPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [licenseInfo, setLicenseInfo] = useState<LicenseInfo | null>(null);
  const [invoices, setInvoices] = useState<LicenseInvoice[]>([]);
  const [companyInfo, setCompanyInfo] = useState<any>(null);

  // Modais
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [pixModalOpen, setPixModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<LicenseInvoice | null>(null);

  const loadData = async (force = false) => {
    try {
      setLoading(true);
      const [statusRes, invoicesRes] = await Promise.allSettled([
        licenseService.getStatus(force),
        licenseService.getInvoices(),
      ]);

      if (statusRes.status === 'fulfilled' && statusRes.value) {
        setLicenseInfo(statusRes.value);
      } else if (statusRes.status === 'rejected') {
        console.error('Falha ao obter status da licença:', statusRes.reason);
        toast.error('Erro ao consultar status da licença no servidor local.');
      }

      if (invoicesRes.status === 'fulfilled' && invoicesRes.value) {
        setInvoices(invoicesRes.value.invoices || []);
        if (invoicesRes.value.license?.company) {
          setCompanyInfo(invoicesRes.value.license.company);
        }
      } else if (invoicesRes.status === 'rejected') {
        console.warn('Faturas da licença não puderam ser carregadas:', invoicesRes.reason);
      }
    } catch (err) {
      console.error('Erro ao carregar dados da licença:', err);
      toast.error('Erro ao carregar dados da licença');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const copyToClipboard = (text: string, msg: string) => {
    navigator.clipboard.writeText(text);
    toast.success(msg);
  };

  const handleOpenPix = (inv: LicenseInvoice) => {
    setSelectedInvoice(inv);
    setPixModalOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'overdue': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'paid': return 'Paga';
      case 'pending': return 'Pendente';
      case 'overdue': return 'Vencida';
      case 'cancelled': return 'Cancelada';
      default: return status;
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <KeyIcon color="primary" /> Gerenciamento da Licença SYSUP
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Consulte a validade, recursos contratados e faturas de mensalidade
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadData(true)}
            disabled={loading}
          >
            Sincronizar
          </Button>
          <Button
            variant="contained"
            startIcon={<KeyIcon />}
            onClick={() => setActivateModalOpen(true)}
          >
            Alterar Licença
          </Button>
        </Box>
      </Box>

      {/* Cards de Visão Geral */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Card Status da Licença */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                STATUS DO SISTEMA
              </Typography>
              {loading ? (
                <Skeleton height={50} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 1 }}>
                  <Chip
                    label={licenseInfo?.valid ? 'LICENÇA ATIVA' : 'BLOQUEADA'}
                    color={licenseInfo?.valid ? 'success' : 'error'}
                    sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}
                  />
                  {licenseInfo?.is_lifetime && (
                    <Chip
                      icon={<LifetimeIcon fontSize="small" />}
                      label="VITALÍCIA"
                      color="secondary"
                      variant="outlined"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </Box>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {licenseInfo?.is_lifetime ? (
                  'Esta instalação possui licença perpétua sem expiração de uso.'
                ) : licenseInfo?.valid_until ? (
                  <>
                    Válida até:{' '}
                    <strong>{new Date(licenseInfo.valid_until).toLocaleDateString('pt-BR')}</strong>
                    {licenseInfo.days_remaining !== null && licenseInfo.days_remaining !== undefined && (
                      <span> ({licenseInfo.days_remaining} dias restantes)</span>
                    )}
                  </>
                ) : (
                  'Licença sem prazo definido ou não configurada.'
                )}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Card Plano e Recursos */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                PLANO CONTRATADO
              </Typography>
              {loading ? (
                <Skeleton height={50} />
              ) : (
                <Typography variant="h5" fontWeight="bold" color="primary.main" sx={{ my: 1 }}>
                  {licenseInfo?.plan || 'Padrão'}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                <Chip size="small" label={`Até ${licenseInfo?.max_clients || '—'} Clientes`} />
                <Chip size="small" label={`Até ${licenseInfo?.max_contracts || '—'} Contratos`} />
                <Chip size="small" label={`Até ${licenseInfo?.max_users || '—'} Usuários`} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card Empresa / Provedor */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BusinessIcon fontSize="small" /> TITULAR DA CONTA
              </Typography>
              {loading ? (
                <Skeleton height={50} />
              ) : (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" noWrap>
                    {companyInfo?.nome_fantasia || companyInfo?.razao_social || 'Empresa Provedora'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    CNPJ/CPF: {companyInfo?.cnpj_cpf || '—'}
                  </Typography>
                </>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, py: 0.5, borderRadius: 1 }}>
                  {licenseInfo?.license_key ? `${licenseInfo.license_key.substring(0, 15)}...` : 'Chave oculta'}
                </Typography>
                {licenseInfo?.license_key && (
                  <Tooltip title="Copiar chave de licença">
                    <IconButton size="small" onClick={() => copyToClipboard(licenseInfo.license_key!, 'Chave copiada!')}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Módulos Habilitados */}
      {licenseInfo?.modules && licenseInfo.modules.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight="bold" color="text.secondary">
                MÓDULOS INCLUSOS:
              </Typography>
              {licenseInfo.modules.map((mod) => (
                <Chip key={mod} label={mod.toUpperCase()} size="small" variant="outlined" color="primary" />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tabela de Faturas */}
      <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptIcon color="action" />
              <Typography variant="h6" fontWeight="bold">
                Faturas & Pagamentos da Licença
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Atualizado automaticamente via integração bancária
            </Typography>
          </Box>

          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Table>
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <TableCell><strong>Nº Fatura</strong></TableCell>
                  <TableCell><strong>Competência</strong></TableCell>
                  <TableCell><strong>Vencimento</strong></TableCell>
                  <TableCell><strong>Valor</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Ações / Pagamento</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                      <TableCell><Skeleton /></TableCell>
                    </TableRow>
                  ))
                ) : invoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {licenseInfo?.is_lifetime
                          ? '♾️ Plano vitalício ativo. Nenhuma mensalidade pendente.'
                          : 'Nenhuma fatura encontrada para esta licença.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  invoices.map((inv) => (
                    <TableRow key={inv.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {inv.invoice_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {inv.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {String(inv.reference_month).padStart(2, '0')}/{inv.reference_year}
                      </TableCell>
                      <TableCell>
                        {new Date(inv.due_date).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          R$ {Number(inv.total).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={getStatusLabel(inv.status)}
                          color={getStatusColor(inv.status)}
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {inv.status === 'paid' ? (
                          <Chip
                            icon={<PaidIcon />}
                            label="PAGO"
                            color="success"
                            variant="outlined"
                            size="small"
                          />
                        ) : (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            {(inv.gateway_pix_copia_cola || inv.gateway_pix_qrcode) && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<QrCodeIcon />}
                                onClick={() => handleOpenPix(inv)}
                              >
                                Pagar via PIX
                              </Button>
                            )}
                            {inv.gateway_boleto_url && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                href={inv.gateway_boleto_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Boleto
                              </Button>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Modal de Pagamento PIX */}
      <Dialog open={pixModalOpen} onClose={() => setPixModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Pagamento Instantâneo PIX
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedInvoice?.invoice_number} — R$ {Number(selectedInvoice?.total || 0).toFixed(2)}
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          {selectedInvoice?.gateway_pix_copia_cola ? (
            <Box sx={{ mb: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#ffffff',
                  borderRadius: 3,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  display: 'inline-flex',
                }}
              >
                <QRCodeSVG
                  value={selectedInvoice.gateway_pix_copia_cola}
                  size={200}
                  level="M"
                  includeMargin={false}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Aponte a câmera do aplicativo do seu banco para o QR Code
              </Typography>
            </Box>
          ) : selectedInvoice?.gateway_pix_qrcode ? (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <img
                src={selectedInvoice.gateway_pix_qrcode}
                alt="QR Code PIX"
                style={{ width: 220, height: 220, borderRadius: 8, background: '#fff', padding: 8 }}
              />
            </Box>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Dados do PIX ainda não disponíveis para esta fatura.
            </Alert>
          )}


          {selectedInvoice?.gateway_pix_copia_cola && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                PIX COPIA E COLA:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  bgcolor: 'action.hover',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  wordBreak: 'break-all',
                  mb: 1.5,
                  maxHeight: 90,
                  overflowY: 'auto',
                }}
              >
                {selectedInvoice.gateway_pix_copia_cola}
              </Paper>
              <Button
                variant="contained"
                fullWidth
                color="primary"
                startIcon={<CopyIcon />}
                onClick={() => copyToClipboard(selectedInvoice.gateway_pix_copia_cola!, 'Código PIX copiado!')}
              >
                Copiar Código PIX
              </Button>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            ⚡ A confirmação é instantânea e a licença é reativada em poucos segundos.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button fullWidth onClick={() => setPixModalOpen(false)} color="inherit">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Alteração de Licença */}
      <LicenseModal
        open={activateModalOpen}
        onClose={() => setActivateModalOpen(false)}
        onSuccess={() => {
          setActivateModalOpen(false);
          loadData(true);
        }}
      />
    </Box>
  );
};

export default MinhaLicencaPage;
